<template>
  <div id="eu-delivered">
    <div id="view-sub-nav" class="d-flex justify-space-between align-center">
      <DateRangePicker default="lastSixMonths" ref="dateRangePicker" @change="onDateRangeChange"></DateRangePicker>
      <div class="d-flex align-center flex-shrink-0">
        <v-chip size="small" color="primary"
          >Found {{ shipmentsWithGeo.length }} of {{ displayData.length }} Filtered Shipments</v-chip
        >
        <v-chip size="small" color="success" class="ml-2">Filtered Shipments Tally {{ grandTotalRevenue }}</v-chip>
        <v-chip v-if="missingGeoCount > 0" size="small" color="warning" class="ml-2"
          >Missing Geo: {{ missingGeoCount }}</v-chip
        >
        <v-switch
          class="ml-6"
          v-model="includeFocRecut"
          :label="includeFocRecut ? 'Including FOC/Recut' : 'Excluding FOC/Recut'"
          hide-details
          @update:model-value="onIncludeFocRecutChange"
        >
        </v-switch>
        <v-switch class="ml-6" v-model="showMap" :label="showMap ? 'Toggle to Chart' : 'Toggle to Map'" hide-details>
        </v-switch>
      </div>
    </div>
    <div v-if="showMap" class="map-frame">
      <div
        v-if="loading"
        class="d-flex justify-center align-center"
        style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          z-index: 1000;
        "
      >
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>
      <!-- EU DELIVERED LOCATION MAP -->
      <l-map id="eu-map" :zoom="zoom" :center="center">
        <l-tile-layer :url="url"></l-tile-layer>
        <l-marker-cluster-group>
          <l-marker v-for="(s, index) in shipmentsWithGeo" :lat-lng="s.latlng" :key="index">
            <l-tooltip>
              <div v-if="s.geoFallback" style="color: red; font-weight: bold; margin-bottom: 4px">Postcode N/A</div>
              <div>
                <span>Order: </span>
                <span>{{ s.orderNumber }}</span>
              </div>
              <div>
                <span>Customer: </span>
                <span>{{ s.customerEmail }}</span>
              </div>
              <div>
                <span>Country: </span>
                <span>{{ getCountryName(s.deliveryCountry) }}</span>
              </div>
              <div v-if="s.deliveryCity">
                <span>City: </span>
                <span>{{ s.deliveryCity }}</span>
              </div>
              <div v-if="s.total">
                <span>Total: </span>
                <span>{{ formatCurrency(s.total) }}</span>
              </div>
              <div>
                <span>Delivered: </span>
                <span>{{ $moment.unix(s.deliveryDate.seconds).format('Do MMM YYYY') }}</span>
              </div>
            </l-tooltip>
          </l-marker>
        </l-marker-cluster-group>
      </l-map>
    </div>
    <!-- EU DELIVERED LOCATION TABLE - BY COUNTRY -->
    <div v-else>
      <div v-if="loading" class="d-flex justify-center align-center" style="height: 400px">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>
      <div v-else>
        <v-container fluid>
          <v-data-table
            :headers="headers"
            :items="countryGroups"
            class="elevation-1"
            :items-per-page="20"
            :sort-by="[{ key: 'orderCount', order: 'desc' }]"
          ></v-data-table>
        </v-container>
      </div>
    </div>
  </div>
</template>

<script type="text/javascript">
import { LMap, LTileLayer, LMarker, LTooltip } from '@vue-leaflet/vue-leaflet'
import { Icon } from 'leaflet'
import DateRangePicker from '@/components/ui/DateRangePicker.vue'
import { ieRoutingKeys } from '@/js/postcodeMapping'
import { db } from '@/js/db'
import { collection, doc, documentId, getDocs, query, updateDoc, where } from 'firebase/firestore'

import LMarkerClusterGroup from '@/components/ui/LMarkerClusterGroup.vue'

delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
})

// EU country names mapping
const EU_COUNTRIES = {
  BE: 'Belgium',
  FR: 'France',
  DE: 'Germany',
  IE: 'Ireland',
  NL: 'Netherlands',
  SE: 'Sweden',
  AT: 'Austria',
  DK: 'Denmark',
  FI: 'Finland',
  IT: 'Italy',
  ES: 'Spain',
  PT: 'Portugal',
  PL: 'Poland',
  CZ: 'Czech Republic',
  LU: 'Luxembourg',
  CH: 'Switzerland'
}

export default {
  name: 'EuDeliveredLocation',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LMarkerClusterGroup,
    DateRangePicker
  },
  data() {
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      zoom: 5,
      center: [50, 10], // Centered on Europe
      orders: [], // Filtered EU orders
      shipmentData: {}, // Map of orderID -> { latlng, postcode, shipmentId }
      showMap: true,
      loading: false,
      missingGeoCount: 0,
      includeFocRecut: false,
      lastDateRange: null
    }
  },
  computed: {
    // Combine order data with shipment data for display
    displayData() {
      return this.orders.map((order) => {
        const shipment = this.shipmentData[order.id]
        return {
          orderID: order.id,
          orderNumber: order.orderNumber,
          customerEmail: order.customerEmail,
          deliveryDate: shipment?.deliveryDate || null,
          total: order.total,
          deliveryCountry: order.deliveryCountry,
          deliveryCity: order.deliveryCity,
          latlng: shipment?.latlng || null,
          shipmentId: shipment?.shipmentId || null,
          postcode: shipment?.postcode || null,
          geoFallback: shipment?.geoFallback || false
        }
      })
    },
    // Filter displayData to only those with geo coordinates
    shipmentsWithGeo() {
      return this.displayData.filter((item) => {
        return item.latlng && Array.isArray(item.latlng) && item.latlng.length === 2
      })
    },
    countryGroups() {
      const groups = {}

      this.orders.forEach((order) => {
        const country = order.deliveryCountry || 'Unknown'
        if (!groups[country]) {
          groups[country] = { count: 0, total: 0 }
        }
        groups[country].count++
        groups[country].total += order.total || 0
      })

      return Object.entries(groups).map(([code, data]) => ({
        countryCode: code,
        country: this.getCountryName(code),
        orderCount: data.count,
        orderTotal: data.total,
        orderTotalFormatted: this.formatCurrency(data.total)
      }))
    },
    headers() {
      return [
        { title: 'Country', key: 'country' },
        { title: 'Code', key: 'countryCode' },
        { title: 'Orders', key: 'orderCount', sortable: true },
        { title: 'Total', key: 'orderTotalFormatted', sortable: true }
      ]
    },
    // Grand total revenue across all EU countries
    grandTotalRevenue() {
      const total = this.orders.reduce((sum, order) => sum + (order.total || 0), 0)
      return this.formatCurrency(total)
    }
  },
  methods: {
    getCountryName(code) {
      return EU_COUNTRIES[code] || code
    },
    formatCurrency(amount) {
      return '£' + this.$numeral(amount).format('0,0.00')
    },
    async fetchOrderData(orderIDs) {
      if (!orderIDs.length) return {}

      const data = {}
      const batches = []
      for (let i = 0; i < orderIDs.length; i += 10) {
        batches.push(orderIDs.slice(i, i + 10))
      }

      await Promise.all(
        batches.map(async (batch) => {
          const snapshot = await getDocs(query(collection(db, 'orders'), where(documentId(), 'in', batch)))
          snapshot.forEach((snap) => {
            const orderDoc = snap.data()
            data[snap.id] = {
              total: orderDoc.total || 0,
              type: orderDoc.type || null,
              region: orderDoc.region || null,
              orderNumber: orderDoc.orderNumber || null,
              customerEmail: orderDoc.customerEmail || null,
              deliveryCountry: orderDoc.customerContactDetails?.deliveryCountry || null,
              deliveryCity: orderDoc.customerContactDetails?.deliveryCity || null,
              deliveryPostcode: orderDoc.customerContactDetails?.deliveryPostcode || null,
              quoteID: orderDoc.quoteID || null,
              versionID: orderDoc.versionID || null,
              foc: false,
              recut: false
            }
          })
        })
      )

      return data
    },
    async fetchVersionFlags(quoteIDs) {
      const result = {}
      if (!quoteIDs.length) return result
      const batches = []
      for (let i = 0; i < quoteIDs.length; i += 10) {
        batches.push(quoteIDs.slice(i, i + 10))
      }
      await Promise.all(
        batches.map(async (batch) => {
          const snapshot = await getDocs(query(collection(db, 'quotes'), where(documentId(), 'in', batch)))
          snapshot.forEach((snap) => {
            result[snap.id] = snap.data().versions || []
          })
        })
      )
      return result
    },
    onDateRangeChange(range) {
      if (range && range.length === 2) {
        const dateRange = {
          start: range[0],
          end: range[1]
        }
        this.bindOrders(dateRange)
      }
    },
    onIncludeFocRecutChange() {
      if (this.lastDateRange) {
        this.bindOrders(this.lastDateRange)
      }
    },
    async bindOrders(dateRange) {
      if (!dateRange || !dateRange.start || !dateRange.end) {
        return
      }

      this.lastDateRange = dateRange
      this.loading = true

      try {
        const startTimestamp = this.$utils.tsFromDate(dateRange.start.toDate())
        const endTimestamp = this.$utils.tsFromDate(dateRange.end.toDate())

        // Step 1: Query shipments by deliveryDate range
        const shipmentsSnapshot = await getDocs(
          query(
            collection(db, 'shipments'),
            where('shipped', '==', true),
            where('deliveryDate', '>=', startTimestamp),
            where('deliveryDate', '<=', endTimestamp)
          )
        )

        const allShipments = shipmentsSnapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }))

        // Step 2: Get unique orderIDs and fetch orders
        const orderIDs = [...new Set(allShipments.filter((s) => s.orderID).map((s) => s.orderID))]
        const orderData = await this.fetchOrderData(orderIDs)

        // Resolve foc/recut from the quote version, not the order doc
        const quoteIDs = [
          ...new Set(
            Object.values(orderData)
              .map((o) => o.quoteID)
              .filter(Boolean)
          )
        ]
        const quoteVersions = await this.fetchVersionFlags(quoteIDs)
        Object.values(orderData).forEach((order) => {
          const versions = quoteVersions[order.quoteID] || []
          const version = versions.find((v) => v.id === order.versionID)
          order.foc = !!(version && version.foc)
          order.recut = !!(version && version.recut)
        })

        // Step 3: Filter orders by type/region/deliveryCountry (FOC/recut excluded unless toggle is on)
        const filteredOrderIDs = Object.entries(orderData)
          .filter(([, order]) => {
            if (order.type !== 'mega-admin') return false
            if (!this.includeFocRecut) {
              if (order.foc) return false
              if (order.recut) return false
            }
            if (order.region !== 'eu') return false
            return order.deliveryCountry && order.deliveryCountry !== 'GB'
          })
          .map(([id]) => id)

        // Step 4: Filter shipments to only those matching filtered orders
        const filteredShipments = allShipments.filter((s) => filteredOrderIDs.includes(s.orderID))

        // Step 5: Build shipmentData map and backfill missing data from orders
        this.shipmentData = {}
        const shipmentUpdates = []

        for (const s of filteredShipments) {
          const order = orderData[s.orderID]
          let postcode = s.postcode
          let needsUpdate = false
          const updateData = {}

          // Backfill missing postcode from order
          if (!postcode && order?.deliveryPostcode) {
            postcode = order.deliveryPostcode
            updateData.postcode = postcode
            needsUpdate = true
          }

          // Queue shipment update if needed
          if (needsUpdate) {
            shipmentUpdates.push({ id: s.id, data: updateData })
          }

          this.shipmentData[s.orderID] = {
            shipmentId: s.id,
            latlng: s.latlng || null,
            postcode: postcode || null,
            deliveryDate: s.deliveryDate,
            geoFallback: false
          }
        }

        // Batch update shipments with backfilled data
        if (shipmentUpdates.length > 0) {
          await Promise.all(shipmentUpdates.map(({ id, data }) => updateDoc(doc(db, 'shipments', id), data)))
        }

        // Build orders array from filtered orderIDs
        this.orders = filteredOrderIDs.map((id) => ({
          id,
          ...orderData[id]
        }))

        // Step 6: Geocode items that need it
        const itemsNeedingGeo = this.displayData.filter((item) => item.postcode && !item.latlng)

        for (const item of itemsNeedingGeo) {
          await this.setLatLngFromPostcode(item)
        }

        // Force Vue reactivity by reassigning shipmentData
        this.shipmentData = { ...this.shipmentData }

        // Count orders that still don't have geo after all attempts
        this.missingGeoOrders = this.displayData.filter(
          (item) => !item.latlng || !Array.isArray(item.latlng) || item.latlng.length !== 2
        )
        this.missingGeoCount = this.missingGeoOrders.length
        if (this.missingGeoCount > 0) {
          for (const item of this.missingGeoOrders) {
            console.log(
              `Order: ${item.orderNumber} - Customer: ${item.customerEmail} - Postcode: ${item.postcode} - Delivery Country: ${item.deliveryCountry} - Delivery City: ${item.deliveryCity}`
            )
          }
        }
      } catch (error) {
        console.error('Error loading EU orders:', error)
      } finally {
        this.loading = false
      }
    },
    async setLatLngFromPostcode(item) {
      try {
        if (!item.postcode || !item.deliveryCountry) return
        // Skip if already has latlng
        if (item.latlng?.length === 2) return

        let lat, lng
        let geoFallback = false
        const country = item.deliveryCountry
        let city = item.deliveryCity
        let cleanPostcode = item.postcode.replace(/\s/g, '')

        // Extract valid postcode from potentially malformed data
        // Dutch: 4 digits + 2 letters (e.g., 1063EC)
        // German: 5 digits (e.g., 10115)
        // French: 5 digits (e.g., 75001)
        // Belgian: 4 digits (e.g., 1000)
        const postcodePatterns = {
          NL: /^(\d{4}[A-Za-z]{2})/,
          DE: /^(\d{5})/,
          FR: /^(\d{5})/,
          BE: /^(\d{4})/,
          AT: /^(\d{4})/,
          IT: /^(\d{5})/,
          ES: /^(\d{5})/,
          PT: /^(\d{4}(-\d{3})?)/,
          PL: /^(\d{2}-?\d{3})/,
          DK: /^(\d{4})/,
          SE: /^(\d{3}\s?\d{2})/,
          FI: /^(\d{5})/,
          CZ: /^(\d{3}\s?\d{2})/,
          LU: /^(\d{4})/,
          CH: /^(\d{4})/,
          IE: /^([A-Za-z]\d{2}\s?[A-Za-z0-9]{4}|\d{2})/
        }

        const pattern = postcodePatterns[country]
        if (pattern) {
          const match = cleanPostcode.match(pattern)
          if (match) {
            // Extract city from remainder if we don't have one
            if (!city && cleanPostcode.length > match[1].length) {
              city = cleanPostcode.slice(match[1].length)
            }
            cleanPostcode = match[1]
          }
        }

        // For Ireland, use routing key lookup directly
        if (country === 'IE' && cleanPostcode && cleanPostcode.length >= 3) {
          const routingKey = cleanPostcode.substring(0, 3).toUpperCase()
          const coords = ieRoutingKeys[routingKey]
          if (coords) {
            lat = coords[0]
            lng = coords[1]
            geoFallback = true
          }
        }

        // Try Zippopotam.us (postcode + country)
        if (!lat) {
          try {
            const response = await fetch(`https://api.zippopotam.us/${country}/${cleanPostcode}`)
            if (response.ok) {
              const data = await response.json()
              if (data.places?.[0]) {
                lat = parseFloat(data.places[0].latitude)
                lng = parseFloat(data.places[0].longitude)
              }
            }
          } catch (e) {
            // Zippopotam.us failed, will try fallbacks
          }
        }

        // Fallback 1: Nominatim with just postcode
        if (!lat && cleanPostcode) {
          try {
            const query = `postalcode=${encodeURIComponent(cleanPostcode)}&format=json&limit=1`
            const response = await fetch(`https://nominatim.openstreetmap.org/search?${query}`, {
              headers: { 'User-Agent': 'MegaAdmin/1.0' }
            })
            if (response.ok) {
              const data = await response.json()
              if (data?.[0]) {
                lat = parseFloat(data[0].lat)
                lng = parseFloat(data[0].lon)
              }
            }
          } catch (e) {
            // Nominatim fallback 1 failed
          }
          await new Promise((resolve) => setTimeout(resolve, 300))
        }

        // Fallback 2: Nominatim with postcode + country
        if (!lat && cleanPostcode) {
          try {
            const query = `postalcode=${encodeURIComponent(
              cleanPostcode
            )}&countrycodes=${country.toLowerCase()}&format=json&limit=1`
            const response = await fetch(`https://nominatim.openstreetmap.org/search?${query}`, {
              headers: { 'User-Agent': 'MegaAdmin/1.0' }
            })
            if (response.ok) {
              const data = await response.json()
              if (data?.[0]) {
                lat = parseFloat(data[0].lat)
                lng = parseFloat(data[0].lon)
              }
            }
          } catch (e) {
            // Nominatim fallback 2 failed
          }
          await new Promise((resolve) => setTimeout(resolve, 300))
        }

        // Fallback 3: Nominatim with postcode + city + country
        if (!lat && cleanPostcode && city) {
          try {
            const query = `postalcode=${encodeURIComponent(cleanPostcode)}&city=${encodeURIComponent(
              city
            )}&countrycodes=${country.toLowerCase()}&format=json&limit=1`
            const response = await fetch(`https://nominatim.openstreetmap.org/search?${query}`, {
              headers: { 'User-Agent': 'MegaAdmin/1.0' }
            })
            if (response.ok) {
              const data = await response.json()
              if (data?.[0]) {
                lat = parseFloat(data[0].lat)
                lng = parseFloat(data[0].lon)
              }
            }
          } catch (e) {
            // Nominatim fallback 3 failed
          }
          await new Promise((resolve) => setTimeout(resolve, 300))
        }

        // Fallback 4: Nominatim with city + country only (imprecise, last resort)
        if (!lat && city) {
          try {
            const query = `city=${encodeURIComponent(city)}&countrycodes=${country.toLowerCase()}&format=json&limit=1`
            const response = await fetch(`https://nominatim.openstreetmap.org/search?${query}`, {
              headers: { 'User-Agent': 'MegaAdmin/1.0' }
            })
            if (response.ok) {
              const data = await response.json()
              if (data?.[0]) {
                lat = parseFloat(data[0].lat)
                lng = parseFloat(data[0].lon)
                geoFallback = true
              }
            }
          } catch (e) {
            // Nominatim fallback 4 failed
          }
        }

        if (lat && lng) {
          // Update shipment in database (only save latlng if it's precise, not fallback)
          if (item.shipmentId && !geoFallback) {
            try {
              await updateDoc(doc(db, 'shipments', item.shipmentId), {
                latlng: [lat, lng]
              })
            } catch (dbError) {
              // Failed to update shipment
            }
          }
          // Update local data reactively
          if (this.shipmentData[item.orderID]) {
            this.shipmentData[item.orderID].latlng = [lat, lng]
            this.shipmentData[item.orderID].geoFallback = geoFallback
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 300))
      } catch (error) {
        // Geocoding failed for this item
        console.error('Error geocoding:', item.postcode, error)
      }
    }
  },
  created() {
    this.$nextTick(() => {
      if (this.$refs.dateRangePicker) {
        this.$refs.dateRangePicker.setPreset(1)
        const twelveMonthRange = this.$refs.dateRangePicker.lastTwelveMonths()
        const dateRange = {
          start: twelveMonthRange[0],
          end: twelveMonthRange[1]
        }
        this.bindOrders(dateRange)
      }
    })
  }
}
</script>

<style lang="scss">
@import '~leaflet/dist/leaflet.css';
@import '~leaflet.markercluster/dist/MarkerCluster.css';
@import '~leaflet.markercluster/dist/MarkerCluster.Default.css';

#eu-delivered {
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
}

#eu-map {
  z-index: 0;
  height: 100%;
  width: 100%;
}

.map-frame {
  position: relative;
  width: 100%;
  height: $analytics-map-height;
}
</style>
